import { EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client/react';
import { App, Modal } from 'antd';
import { useForm } from 'antd/es/form/Form';
import type React from 'react';
import { useEffect } from 'react';
import { LoadingIndicator } from '../../../../components/LoadingIndicator.tsx';
import type { FragmentType } from '../../../../graphql/generated';
import type { CatalogCreateInput, CatalogUpdateInput } from '../../../../graphql/generated/graphql.ts';
import { CatalogForm } from './CatalogForm';
import type { ParameterFragment } from './CatalogForm.gql';
import { CATALOG_QUERY, UPDATE_CATALOG_MUTATION } from './UpdateCatalogModal.gql';
export const UpdateCatalogModal: React.FC<{
  catalogId: string | null;
  parameterFragments: FragmentType<typeof ParameterFragment>[];
  onClose: () => void;
  onSuccess: () => void;
}> = ({ catalogId, parameterFragments, onClose, onSuccess }) => {
  const [updateCatalogMutation, { loading: mutationLoading }] = useMutation(UPDATE_CATALOG_MUTATION);
  const [form] = useForm<CatalogCreateInput | CatalogUpdateInput>();
  const { message } = App.useApp();

  const { data, loading } = useQuery(CATALOG_QUERY, {
    variables: {
      catalogId: catalogId ?? '',
    },
    skip: catalogId === null,
  });

  const update = async (values: CatalogUpdateInput) => {
    try {
      await updateCatalogMutation({
        variables: {
          id: catalogId ?? '',
          input: { ...values },
        },
      });

      message.success('Parameterkatalog wurde aktualisiert');
      onSuccess();
      onClose();
    } catch {
      message.error('Parameterkatalog konnte nicht aktualisiert werden');
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      name: data?.catalog?.name ?? '',
      description: data?.catalog?.description ?? '',
      parameterIds: data?.catalog?.catalogParameters.map(p => p.parameter.id) ?? [],
    });
  }, [form, data]);

  return (
    <Modal
      width={768}
      title="Parameterkatalog bearbeiten"
      okText="Aktualisieren"
      okButtonProps={{ icon: <EditOutlined /> }}
      open={!!catalogId}
      onCancel={onClose}
      onOk={form.submit}
      confirmLoading={mutationLoading}
      destroyOnHidden
    >
      {loading || !catalogId /* performance improvement */ ? (
        <LoadingIndicator height="200px" />
      ) : (
        <CatalogForm form={form} onFinish={update} parameterFragments={parameterFragments} />
      )}
    </Modal>
  );
};
